/**
 * The production release number, and the dev/stage deploy number, as the
 * SERVING API reports them.
 *
 * The status bar shows the release as the third version component
 * (`1.0.<N>`). The number baked into the bundle at build time lags the API
 * after an `--api-only` release, which skips the frontend build, so App.vue
 * asks the API instead and keeps the bundle's number as the fallback.
 *
 * The API learns the number from tools/deploy-production.sh (the TF_RELEASE
 * build arg) and reports it as `release` on `GET /health/version`: an integer
 * on a production release image, null everywhere else. Dev and stage deploys
 * are numbered the same way (TF_DEPLOY, reported as `deploy`) and take the
 * place of the time in the version stamp; see App.vue `versionStamp`.
 *
 * BARE `axios`, NOT `@/axios`. The endpoint is public and sits at the API
 * ORIGIN, not under `/api`. The app instance would attach the session bearer
 * to a request that needs none, and that header would also turn a simple
 * cross-origin GET into a preflighted one.
 */
import axios from "axios";

// Long enough for a cold API, short enough that a hung request is abandoned
// rather than held open for the life of the page. The bar never waits on it.
const TIMEOUT_MS = 10000;

/**
 * `<api origin>/health/version`, from the build-time API base
 * (`https://api.threadflow.com/api` -> `https://api.threadflow.com`). A
 * relative or missing base yields an origin-relative path.
 */
export function healthVersionUrl(apiBaseUrl) {
  const origin = String(apiBaseUrl || "")
    .replace(/\/+$/, "")
    .replace(/\/api$/, "");
  return `${origin}/health/version`;
}

/**
 * Ask the API which release and deploy it is, in ONE request.
 *
 * Resolves to `{ release, deploy }` when the API answered. `release` is an
 * integer on a production release image, `deploy` a positive integer on a
 * numbered dev or stage deploy (the `deploy/<server>/<N>` tag); either is
 * null where it does not apply. Resolves to `null` when it could not be read
 * (network, CORS, timeout, not the version payload). Never rejects: the
 * caller is page chrome and must not have to handle a failure.
 */
export async function fetchApiRelease(apiBaseUrl) {
  try {
    const resp = await axios.get(healthVersionUrl(apiBaseUrl), {
      timeout: TIMEOUT_MS,
    });
    const body = resp?.data;
    if (!body || typeof body !== "object") return null;
    const release = body.release;
    const deploy = body.deploy;
    return {
      release: Number.isInteger(release) && release >= 0 ? release : null,
      deploy: Number.isInteger(deploy) && deploy > 0 ? deploy : null,
    };
  } catch (e) {
    return null;
  }
}
